import * as React from 'react';
import * as c from 'models/constants';
import { Button, ErrorSummary, Modal, WhiteButton } from 'components';
import { translate } from 'react-i18next';
import { ITranslateProps } from 'models/common';
import { IOffice } from 'models/shop/office';
import { connect, Dispatch } from 'react-redux';
import { IStore } from 'redux/IStore';
import { getServiceTemplatesByShop, saveShopServiceTemplate } from 'redux/modules/shop/servicetemplates';
import { IShopCard } from 'models/shop/shopcard';
import { LoadingOverlayWrapper } from 'components/Loader';
import { closeModal } from 'redux/modules/modal';
import { IShopServiceTemplate } from 'models/shop/serviceTemplates';
import { BookingInputField } from 'components/FormControls';
import renderIcon from 'helpers/renderIcon';
import { domainValuesMap } from 'redux/modules/domainvalues';

interface ISaveShopServiceTemplateOwnProps {
	shopId: IShopCard['id'];
	officeId: IOffice['id'];
}

interface IState {
	mode?: 'new' | 'existing';
	name: IShopServiceTemplate['templateName'];
}

interface IProps
	extends ISaveShopServiceTemplateOwnProps,
		ITranslateProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class SaveShopServiceTemplateModal extends React.Component<IProps, IState> {
	public constructor(props: IProps) {
		super(props);

		this.state = {
			mode: 'new',
			name: '',
		};

		this.changeName = this.changeName.bind(this);
		this.save = this.save.bind(this);
	}

	public componentDidMount(): void {
		this.props.loadTemplates();
		this.props.loadServiceCategories();
	}

	public renderChoice(): JSX.Element {
		const { t } = this.props;

		return (
			<>
				<button className="clickableCard" onClick={() => this.setState({ mode: 'new' })}>
					<div className="text-container">
						<p className="main">{t('User_ShopCard_Services_SaveAsTemplate_New_Button')}</p>
					</div>
					{renderIcon('add', 32, 32)}
				</button>
			</>
		);
	}

	private get isValid(): boolean {
		const { mode, name } = this.state;

		if (!mode || !name) {
			return false;
		}

		if (mode === 'existing') {
			const validNames = (this.props.templates || []).map((t) => t.templateName);

			if (!validNames.includes(name)) {
				return false;
			}
		}

		if (mode === 'new') {
			return this.getUrlUnsafeCharacter(this.state.name) == undefined;
		}

		return true;
	}

	private get templateAlreadyExists(): boolean {
		const { name } = this.state;
		const validNames = (this.props.templates || []).map((t) => t.templateName);

		if (validNames.includes(name)) {
			return true;
		} else {
			return false;
		}
	}

	private getUrlUnsafeCharacter(str: string) {
		return str?.split('').find((c) => !c.match(/[a-zA-Z0-9._ -]/));
	}

	private cleanUpNameForUrl(str: string) {
		return str
			?.split('')
			.filter((c) => c.match(/[a-zA-Z0-9._ -]/))
			.join('')
			.replace(/^ */, '')
			.replace(/ *$/, '');
	}

	private save() {
		const { mode, name } = this.state;

		if (!this.isValid) {
			return;
		}

		// Open overwrite step if name is the same as one of the existing templates
		if (mode === 'new' && this.templateAlreadyExists) {
			this.setState({ mode: 'existing' });
			return;
		}

		this.props.saveTemplate(mode, this.cleanUpNameForUrl(name));
	}

	public changeName(e: React.ChangeEvent<HTMLInputElement>) {
		this.setState({ name: e.target.value || '' });
	}

	public renderNew(): JSX.Element {
		const { t } = this.props;
		const urlUnsafe = this.getUrlUnsafeCharacter(this.state.name);

		return (
			<>
				<div className="saveTemplateModal__headerContainer">
					<h1 className="saveTemplateModal__title">{this.modalTitle}</h1>
					<div className="saveTemplateModal__closeButton" onClick={this.props.closeModal}>
						{renderIcon('cancel', 20, 20)}
					</div>
				</div>

				<BookingInputField
					label={t('User_ShopCard_Services_SaveAsTemplate_NameTemplate')}
					input={{ value: this.state.name }}
					onChangeAction={this.changeName}
					meta={
						urlUnsafe
							? { touched: true, error: `${t('TemplateBusinessRules_NameIllegalCharacter')}"${urlUnsafe}"` }
							: undefined
					}
					information={t('User_ShopCard_Services_SaveAsTemplate_NameTemplate')}
					placeholder={t('User_ShopCard_Services_SaveAsTemplate_SummerPriceList')}
				/>
			</>
		);
	}

	public renderExisting(): JSX.Element {
		const { t } = this.props;
		return (
			<div>
				<div className="saveTemplateModal__headerContainer">
					<div className="saveTemplateModal__titleContainer">
						{renderIcon('arrow_left', 18, 18, 'saveTemplateModal__backButton', () => {
							this.setState({ mode: 'new' });
						})}
						<h1 className="saveTemplateModal__title">{this.modalTitle}</h1>
					</div>

					<div className="saveTemplateModal__closeButton" onClick={this.props.closeModal}>
						{renderIcon('cancel', 20, 20)}
					</div>
				</div>
				<p className="muted">{t('User_ShopCard_Services_SaveAsTemplate_Existing_Warning')}</p>
			</div>
		);
	}

	public renderModalFooter(): JSX.Element {
		const { t } = this.props;
		const { mode } = this.state;
		const buttons = [
			<WhiteButton key="cancel" onClick={this.props.closeModal}>
				{t('BSPortal_Common_Cancel')}
			</WhiteButton>,
		];

		if (mode === 'existing' || mode === 'new') {
			buttons.push(
				<Button key="save" disabled={!this.isValid} onClick={this.save}>
					{t('BSPortal_Common_Save')}
				</Button>,
			);
		}

		return <div className="modalButtonContainer">{buttons}</div>;
	}

	private renderModal(): JSX.Element {
		switch (this.state.mode) {
			case 'new':
				return this.renderNew();
			case 'existing':
				return this.renderExisting();
			default:
				return this.renderChoice();
		}
	}

	private get modalTitle(): string {
		const { t } = this.props;
		switch (this.state.mode) {
			case 'new':
				return t('User_ShopCard_Services_SaveAsTemplate_New_Title');
			case 'existing':
				return t('BSPortal_Common_ConfirmationDialog_Sure');
			default:
				return t('User_ShopCard_Services_SaveAsTemplate_Title');
		}
	}

	public render() {
		const { isFetching, closeModal, error, message } = this.props;
		return (
			<Modal
				modalClass="modal-with-form card saveTemplateModal"
				size="small"
				closeModal={closeModal}
				footer={this.renderModalFooter()}
			>
				<ErrorSummary error={error} message={message} />
				<LoadingOverlayWrapper active={isFetching}>{this.renderModal()}</LoadingOverlayWrapper>
			</Modal>
		);
	}
}

const mapStateToProps = (state: IStore, ownProps: ISaveShopServiceTemplateOwnProps) => ({
	templates: state.shopServiceTemplates.shopServiceTemplates,
	isFetching: state.shopServiceTemplates.isFetching,
	services: (state.shopBookingTemplates.services || []).filter(
		(s) => s.shopId === ownProps.shopId && s.officeId === ownProps.officeId,
	),
	serviceGroups: (state.serviceGroups.serviceGroups || []).filter((sg) => sg.officeId === ownProps.officeId),
	error: state.shopServiceTemplates.error,
	message: state.shopServiceTemplates.message,
	serviceCategories: state.domainValues.serviceCategories,
});

const mapDispatchToProps = (dispatch: Dispatch<IStore>, ownProps: ISaveShopServiceTemplateOwnProps) => ({
	loadServiceCategories: () => dispatch(domainValuesMap.serviceCategories()),
	loadTemplates: () => dispatch(getServiceTemplatesByShop(ownProps.shopId)),
	saveTemplate: (mode: 'new' | 'existing', templateName: IState['name']) =>
		dispatch(
			saveShopServiceTemplate(mode, {
				officeId: ownProps.officeId,
				templateName,
			}),
		).then((success) => (success ? dispatch(closeModal()) : null)),
	closeModal: () => dispatch(closeModal()),
});

const SaveShopServiceTemplate = connect(mapStateToProps, mapDispatchToProps)(SaveShopServiceTemplateModal);

export { SaveShopServiceTemplate, ISaveShopServiceTemplateOwnProps };
